/**
 * Readings each person pins to their farm home screen. Kept on the server per user, so the
 * same home shows on their phone and computer. Changes show straight away and are saved in
 * the background; if saving fails the last saved list comes back.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { api } from "./api.ts";

export interface Pin {
  devEui: string;
  port: string;
}

interface HomePins {
  pins: Pin[];
  isPinned: (devEui: string, port: string) => boolean;
  toggle: (devEui: string, port: string) => void;
  move: (index: number, by: -1 | 1) => void;
  error: string;
}

const Ctx = createContext<HomePins>({ pins: [], isPinned: () => false, toggle: () => undefined, move: () => undefined, error: "" });

export function HomePinsProvider({ initial, children }: { initial: Pin[] | undefined; children: ReactNode }) {
  const [pins, setPins] = useState<Pin[]>(initial ?? []);
  const [error, setError] = useState("");
  const save = useCallback((next: Pin[], before: Pin[]) => {
    setPins(next);
    setError("");
    api.setHome(next).catch((e: unknown) => {
      setPins(before);
      setError(e instanceof Error ? e.message : "Couldn't save your home screen.");
    });
  }, []);
  const value: HomePins = {
    pins,
    error,
    isPinned: (devEui, port) => pins.some((p) => p.devEui === devEui && p.port === port),
    toggle: (devEui, port) =>
      save(pins.some((p) => p.devEui === devEui && p.port === port) ? pins.filter((p) => !(p.devEui === devEui && p.port === port)) : [...pins, { devEui, port }], pins),
    move: (i, by) => {
      const j = i + by;
      if (j < 0 || j >= pins.length) return;
      const next = [...pins];
      [next[i], next[j]] = [next[j]!, next[i]!];
      save(next, pins);
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useHomePins = () => useContext(Ctx);
